import { SelectedFilters } from "@/routes/Frontend/FuturebilityCockpit/hooks";
import { DtoCubeDataResponse, GenericObject } from "@/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CATEGORY_ICONS } from "../../../components/ChallengeRow";
import { MenuButton } from "../../../components/MenuButton";
import { SolutionJourneyTabs } from "../../../components/SolutionJourneyTabs";
import { Cube } from "../../../components/Сube";
import { CubeSideName } from "../../../components/Сube/types";
import { useSolutionCategoryPageData } from "../../../hooks/useSolutionCategoryPageData";
import { AiResponse, Category, CategoryNavItem } from "../../../types";
import {
  calculatePercentagesForNumbers,
  formatAiHtmlString,
  getAnswerSuggestionForSolutionModule,
  renderNumber,
  translateErrorCode,
} from "../../../utils";
import { GradientCircularProgress } from "../../../../../../components/Loader";
import { MIN_REPLIES_FOR_AI_GENERATION } from "../../../constants";

import "./style.scss";

export interface CategoryPageProps {
  category: CategoryNavItem;
  onHomeClick: () => void;
  rawData?: DtoCubeDataResponse;
  currentTestId: string;
  selectedFilters: SelectedFilters | undefined;
}

export const CategoryPage = ({
  category,
  onHomeClick,
  rawData,
  currentTestId,
  selectedFilters,
}: CategoryPageProps) => {
  const { t } = useTranslation();
  const [selectedCubeSide, setSelectedCubeSide] = useState<CubeSideName>("1");
  const [hoveredCubeSide, setHoveredCubeSide] = useState<CubeSideName>();
  const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false);
  const [aiSuggestionData, setAiSuggestionData] =
    useState<GenericObject<AiResponse> | null>(null);

  const { cubeSidesData, solutionJourney, aiSummaryRequestData } =
    useSolutionCategoryPageData({
      category: category as Category,
      rawData,
      currentTestId,
      selectedFilters,
    });

  const sidesSaturation = calculatePercentagesForNumbers(
    Object.fromEntries(
      Object.entries(cubeSidesData).map(([key, data]) => [key, data?.value])
    )
  );

  const [selectedSolutionJourneyId, setSelectedSolutionJourneyId] = useState<
    string | undefined
  >(solutionJourney[0].id);

  const [selectedSolutionJourneyItem, setSelectedSolutionJourneyItem] =
    useState<string | undefined>(undefined);

  const selectedSolutionJourneyData = solutionJourney.find(
    (journey: any) => journey.id === selectedSolutionJourneyId
  );
  const selectedSolutionJourneyItemData =
    selectedSolutionJourneyData?.items.find(
      (item: any) => item.id === selectedSolutionJourneyItem
    );

  useEffect(() => {
    const fetchSuggestion = async () => {
      setIsAiSummaryLoading(true);
      const suggestionData = await getAnswerSuggestionForSolutionModule(
        aiSummaryRequestData[selectedCubeSide]
      );
      setAiSuggestionData(suggestionData.summaryData);
      setIsAiSummaryLoading(false);
    };
    if (
      (cubeSidesData[selectedCubeSide]?.value ?? 0) >=
      MIN_REPLIES_FOR_AI_GENERATION
    ) {
      fetchSuggestion();
    } else {
      setAiSuggestionData(null);
    }
  }, [selectedCubeSide, selectedFilters]); //eslint-disable-line react-hooks/exhaustive-deps

  const renderCategoryInfo = (cubeSide: CubeSideName) => {
    const value = cubeSidesData?.[cubeSide]?.value ?? 0;
    const title = cubeSidesData?.[cubeSide]?.title ?? "";
    const isActive =
      selectedCubeSide === cubeSide || hoveredCubeSide === cubeSide;

    return (
      <div
        className={`side-score ${isActive ? "active" : ""}`}
        onClick={() => setSelectedCubeSide(cubeSide)}
      >
        <div className="title">{title}</div>
        <div className="value">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="23"
            height="26"
            viewBox="0 0 23 26"
            fill="none"
          >
            <path
              d="M21.6954 24.249L21.6954 21.6934C21.6954 20.3377 21.1569 19.0377 20.1984 18.0791C19.2398 17.1205 17.9397 16.582 16.5841 16.582L6.36136 16.582C5.00574 16.582 3.70565 17.1205 2.74708 18.0791C1.78852 19.0377 1.25 20.3377 1.25 21.6934L1.25 24.249"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.4702 11.4726C14.2932 11.4726 16.5816 9.18423 16.5816 6.36132C16.5816 3.53842 14.2932 1.25 11.4702 1.25C8.64732 1.25 6.35889 3.53842 6.35889 6.36132C6.35889 9.18423 8.64732 11.4726 11.4702 11.4726Z"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {renderNumber(value, false)}
        </div>
      </div>
    );
  };

  const renderAiSuggestion = () => {
    if (isAiSummaryLoading) {
      return (
        <div className="description-full">
          <GradientCircularProgress />
        </div>
      );
    }

    if (aiSuggestionData && selectedSolutionJourneyItemData) {
      const aiSuggestionDataCategory =
        aiSuggestionData[selectedSolutionJourneyItemData.id];
      if (aiSuggestionDataCategory?.error) {
        return (
          <div className="description-full">
            {translateErrorCode(aiSuggestionDataCategory?.error.code ?? "", t)}
          </div>
        );
      } else {
        return (
          <div
            className="description-full"
            dangerouslySetInnerHTML={{
              __html: formatAiHtmlString(
                aiSuggestionData[selectedSolutionJourneyItemData.id]?.summary ??
                  ""
              ),
            }}
          ></div>
        );
      }
    } else {
      return (
        <div className="tab-content">
          {t("frontend.futurebilityCockpit.notEnoughData")}
        </div>
      );
    }
  };

  return (
    <div className="solution-category-page">
      <div className="left">
        <div className="category-view">
          <div className="cube-category-row">
            <MenuButton
              variant="arrow-left"
              text={t("frontend.futurebilityCockpit.back", "Back")}
              onClick={onHomeClick}
            />
            <div className="cube-category-title">
              {CATEGORY_ICONS[category]}{" "}
              {t(`frontend.futurebilityCockpit.categories.${category}.title`)}
            </div>
          </div>
          <div className="extended-cube-container">
            <div className="row justify-center">{renderCategoryInfo("2")}</div>
            <div className="row justify-center">
              <div className="side-group left">
                {renderCategoryInfo("5")}
                {renderCategoryInfo("4")}
              </div>
              <div className="cube-wrapper">
                <Cube
                  greyOutOtherSidesOnHover
                  values={{ 1: 100, 2: 100, 3: 100, 4: 100, 5: 100, 6: 100 }}
                  sidesSaturation={sidesSaturation}
                  selectedSides={[selectedCubeSide]}
                  onSideHover={side => {
                    setHoveredCubeSide(side);
                  }}
                  onSideClick={setSelectedCubeSide}
                />
              </div>
              <div className="side-group right">
                {renderCategoryInfo("6")}
                {renderCategoryInfo("3")}
              </div>
            </div>
            <div className="row justify-center">{renderCategoryInfo("1")}</div>
          </div>
        </div>
      </div>
      <div className="right">
        <div className="right-title">
          {t(
            "frontend.futurebilityCockpit.solution.journey.title",
            "Solution Journey"
          )}
        </div>
        <SolutionJourneyTabs
          onJourneyClicked={(journeyId, journeyItemId) => {
            setSelectedSolutionJourneyId(journeyId);
            setSelectedSolutionJourneyItem(journeyItemId);
          }}
          activeJourneyId={selectedSolutionJourneyId}
          activeJourneyItemId={selectedSolutionJourneyItem}
          journey1={solutionJourney[0]}
          journey2={solutionJourney[1]}
          journey3={solutionJourney[2]}
        />
        <div className="solution-explanations">
          {selectedSolutionJourneyItemData === undefined ? (
            <>
              <div
                className="main-title"
                style={{ color: selectedSolutionJourneyData?.color }}
              >
                {selectedSolutionJourneyData?.summary.title}
              </div>
              <div className="items-list">
                {selectedSolutionJourneyData?.summary.items.map(item => (
                  <div
                    className="item"
                    key={item.id}
                    onClick={() => {
                      setSelectedSolutionJourneyId(
                        selectedSolutionJourneyData?.id
                      );
                      setSelectedSolutionJourneyItem(item.id);
                    }}
                  >
                    <div
                      className="label"
                      style={{ color: selectedSolutionJourneyData?.color }}
                    >
                      {item.label}
                    </div>
                    <div
                      className="description"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="item-details">
              <div className="item-title">
                {`${selectedSolutionJourneyItemData.label} - ${selectedSolutionJourneyItemData.secondaryLabel}`}
              </div>
              <div className="description-full">{renderAiSuggestion()}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
